<template>
	<Col v-if="spaceMembersThatCanUseFeature?.length > 0" class="space-collaborators-container flex-justify-center" :class="{ open: isOpen }">
		<Row @click.native="() => (isOpen = !isOpen)" class="width-100 activator flex-align-center flex-justify-space-between">
			<h5>{{ sharedWithExplanation }}</h5>
			<v-icon :class="{ rotate: isOpen }" color="#64666A" size="16">mdi-chevron-down</v-icon>
		</Row>
		<v-expand-transition>
			<Col v-if="isOpen" class="mt-1">
				<ShareFeatureItemAvatarAndName
					class="mb-4"
					:key="itemId"
					v-bind="getShareFeatureItemAvatarProps(itemId)"
					v-for="itemId in spaceMembersThatCanUseFeature"
				/>
			</Col>
		</v-expand-transition>
	</Col>
</template>
<script>
import { mapGetters } from 'vuex';
import { pluralize } from '@/utils/language-service';
import ProjectsService from '@/utils/projects-service';
import { teamMembers } from '@/store/modules/account/getters';
import { groups } from '@/store/modules/accountGroups/getters';
import { teamMemberByUserId } from '@/store/modules/account/getters';
import { projectSharedData } from '@/store/modules/projects/getters';
import { setProjectShareData } from '@/store/modules/projects/actions';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppAvatarService from '@/components/common/AppAvatar/app-avatar-service.js';
import ShareFeatureItemAvatarAndName from '@/components/dialogs/ShareFeatureDialog/ShareFeatureItemAvatarAndName.vue';
export default {
	name: 'ShareFeatureDialogSpaceMembers',
	props: {
		projectId: { type: Number, required: true },
	},
	components: {
		ShareFeatureItemAvatarAndName,
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ projectSharedData, groups, teamMembers, teamMemberByUserId }),
		spaceMembersThatCanUseFeature() {
			return [...this.sharedUsersWithCollaboratorAccess, ...this.sharedGroupsWithCollaboratorAccess];
		},
		sharedProjectData() {
			return this.projectSharedData(this.projectId) || {};
		},
		sharedUsersWithCollaboratorAccess() {
			return (
				(this.sharedProjectData.shared_users || [])
					.filter(this.canSpaceMemberUseFeature)
					.map(({ id }) => id)
					.filter((userId) => userId !== this.user.id) || []
			);
		},
		sharedGroupsWithCollaboratorAccess() {
			return this.sharedProjectData.shared_groups?.filter(this.canSpaceMemberUseFeature)?.map(({ id }) => id) || [];
		},
		amountOfUsersThatCanUseFeature() {
			return this.sharedUsersWithCollaboratorAccess?.length || 0;
		},
		amountOfGroupsThatCanUseFeature() {
			return this.sharedGroupsWithCollaboratorAccess?.length || 0;
		},
		sharedWithExplanation() {
			return `${this.amountOfUsersThatCanUseFeature ? pluralize('teammate', this.amountOfUsersThatCanUseFeature, true) : ''} ${
				this.amountOfGroupsThatCanUseFeature
					? (this.amountOfUsersThatCanUseFeature ? 'and ' : '') + pluralize('group', this.amountOfGroupsThatCanUseFeature, true)
					: ''
			} can see and use this feature via space permissions.`;
		},
	},
	methods: {
		...mapActionsShorthand({ setProjectShareData }),
		canSpaceMemberUseFeature(user) {
			return ProjectsService.canSpaceMemberRunFeature(user.role);
		},
		getUser(itemId) {
			return this.teamMemberByUserId(itemId);
		},
		getUserFullName(user) {
			return AppAvatarService.getUserFullName(user) || user.email;
		},
		getGroup(itemId) {
			return this.groups.find(({ id }) => id === itemId);
		},
		getGroupFullName(group) {
			return group?.name;
		},
		getShareFeatureItemAvatarProps(itemId) {
			const group = this.getGroup(itemId);
			const user = this.getUser(itemId);
			const item = user || group;
			const isGroup = !!group;
			const name = isGroup ? this.getGroupFullName(item) : this.getUserFullName(item);
			return { isGroup, item, name };
		},
	},
	async created() {
		await this.setProjectShareData({ id: this.projectId, force: true });
	},
	data() {
		return { isOpen: false };
	},
};
</script>
<style scoped lang="scss">
.activator {
	cursor: pointer;
	transition: var(--transition-time);
}
.rotate {
	transform: rotate(-180deg);
}
.space-collaborators-container {
	padding: 0px 8px;
	min-height: 23px;
	border-radius: 5px;
	background: #fbfbfd;
	border: 1px solid #d6ddea;
	transition: var(--transition-time);
	&:hover {
		@extend .animate-activator;
	}
	&.open {
		@extend .animate-activator;
	}
}
.animate-activator {
	.activator {
		padding-top: 10px;
		padding-bottom: 10px;
		transition: var(--transition-time);
	}
}
.user-name {
	color: #292829;
}
</style>
